"use client";

import { useState } from "react";
import type { ChatMessage, WorkflowDebugSummary } from "@/lib/api";
import { undoChatAction } from "@/lib/api";
import { useDataRefresh } from "@/lib/store";
import { updatePreferenceMemory } from "@/lib/settings-api";
import { formatTime } from "@/lib/utils";

interface Props {
  message: ChatMessage;
  onSuggestionSelect?: (suggestion: string) => void;
  onActionClick?: (actionId: string, actionType: string) => void;
}

// WIP-1 § 8-7: shape of `data.undo_action` emitted by Coach / Planner /
// Curator Skills when they autonomously commit an entity. Used to render
// the inline Undo chip below the message bubble.
interface UndoActionPayload {
  type: string;
  entity_id: string;
  label?: string;
  extra?: Record<string, unknown>;
}

function readUndoAction(data: Record<string, unknown> | undefined): UndoActionPayload | null {
  if (!data) return null;
  const raw = data.undo_action;
  if (!raw || typeof raw !== "object") return null;
  const candidate = raw as Record<string, unknown>;
  const type = typeof candidate.type === "string" ? candidate.type : null;
  const entityId = typeof candidate.entity_id === "string" ? candidate.entity_id : null;
  if (!type || !entityId) return null;
  const label = typeof candidate.label === "string" ? candidate.label : "Undo";
  const extra =
    candidate.extra && typeof candidate.extra === "object"
      ? (candidate.extra as Record<string, unknown>)
      : undefined;
  return { type, entity_id: entityId, label, extra };
}

/** Intent badge color mapping */
const INTENT_COLORS: Record<string, string> = {
  coaching: "bg-blue/15 text-blue",
  decision_help: "bg-amber/15 text-amber",
  task_create: "bg-accent/15 text-accent",
  task_complete: "bg-sage-pale text-forest-med",
  task_decompose: "bg-accent/15 text-accent",
  thought_capture: "bg-blue/15 text-blue",
  content: "bg-blue/15 text-blue",
  daily_planning: "bg-blue/15 text-blue",
  goal_progress: "bg-amber/15 text-amber",
  blended: "bg-sage-pale text-forest-med",
  question: "bg-muted/10 text-text-light",
  greeting: "bg-muted/10 text-muted",
};

/** Agent display config */
const AGENT_CONFIG: Record<string, { initial: string; label: string; color: string }> = {
  curator: { initial: "C", label: "Curator", color: "#3B82F6" },
  coach: { initial: "K", label: "Coach", color: "#7C3AED" },
  planner: { initial: "P", label: "Planner", color: "#10B981" },
  analyst: { initial: "A", label: "Analyst", color: "#D97706" },
  companion: { initial: "V", label: "Companion", color: "#B87D7D" },
};

const DEFAULT_AGENT = { initial: "DE", label: "DecisionEase", color: "#3E5E3A" };

/** Render inline markdown: **bold**, *italic*, and line breaks */
function renderMarkdown(text: string) {
  return text.split("\n").map((line, i) => (
    <p key={i} className={line === "" ? "h-2" : "mb-1"}>
      {line.split(/(\*\*.*?\*\*|\*.*?\*)/).map((part, j) => {
        if (part.startsWith("**") && part.endsWith("**")) {
          return <strong key={j}>{part.slice(2, -2)}</strong>;
        }
        if (part.startsWith("*") && part.endsWith("*") && part.length > 2) {
          return <em key={j}>{part.slice(1, -1)}</em>;
        }
        return <span key={j}>{part}</span>;
      })}
    </p>
  ));
}

function splitChatSentences(value: string): string[] {
  return value
    .split(/(?<=[.!?])\s+/)
    .map((part) => part.trim())
    .filter(Boolean);
}

function buildAssistantCopyView(
  text: string,
  options?: { hasStructuredFollowUp?: boolean }
): { visible: string; hidden: string | null } {
  const compact = text.replace(/\n{3,}/g, "\n\n").replace(/\s+\n/g, "\n").trim();
  if (!compact) {
    return { visible: "", hidden: null };
  }

  const maxChars = options?.hasStructuredFollowUp ? 210 : 320;
  const paragraphs = compact
    .split(/\n{2,}/)
    .map((part) => part.trim())
    .filter(Boolean);

  if (paragraphs.length > 2) {
    return {
      visible: paragraphs.slice(0, 2).join("\n\n"),
      hidden: paragraphs.slice(2).join("\n\n"),
    };
  }

  if (compact.length > maxChars) {
    const sentences = splitChatSentences(compact);
    if (sentences.length > 2) {
      return {
        visible: sentences.slice(0, 2).join(" "),
        hidden: sentences.slice(2).join(" "),
      };
    }
  }

  return { visible: compact, hidden: null };
}

function formatIntentLabel(value: string): string {
  const labels: Record<string, string> = {
    coaching: "coaching",
    decision_help: "decision help",
    task_create: "task",
    task_complete: "done",
    task_decompose: "breakdown",
    thought_capture: "capture",
    content: "content",
    daily_planning: "planning",
    goal_progress: "goal review",
    question: "question",
    greeting: "greeting",
    blended: "blended",
  };
  return labels[value] || value.replace(/_/g, " ");
}

function blendedIntentLabel(intents: string[]): string {
  const normalized = Array.from(new Set(intents.filter(Boolean)));
  const key = normalized.sort().join("|");
  const labels: Record<string, string> = {
    "coaching|daily_planning": "support + plan",
    "coaching|decision_help": "support + decide",
    "coaching|daily_planning|decision_help": "support + choose today",
    "daily_planning|goal_progress": "review + plan",
    "coaching|task_decompose": "support + steps",
  };
  return labels[key] || "blended";
}

function getWorkflowDebug(data?: Record<string, unknown>): WorkflowDebugSummary | null {
  const raw = data?.workflow_debug;
  if (!raw || typeof raw !== "object") return null;

  const candidate = raw as Partial<WorkflowDebugSummary>;
  if (!candidate.node_statuses || !candidate.merge_events) return null;

  return {
    route_type:
      typeof candidate.route_type === "string" ? candidate.route_type : "",
    route_reason:
      typeof candidate.route_reason === "string" ? candidate.route_reason : "",
    intents: Array.isArray(candidate.intents)
      ? candidate.intents.map((item) => String(item))
      : [],
    primary_intent:
      typeof candidate.primary_intent === "string"
        ? candidate.primary_intent
        : "",
    routed_to:
      typeof candidate.routed_to === "string" ? candidate.routed_to : "",
    workflow_name:
      typeof candidate.workflow_name === "string"
        ? candidate.workflow_name
        : "",
    classifier_confidence:
      typeof candidate.classifier_confidence === "number"
        ? candidate.classifier_confidence
        : null,
    classifier_candidates: Array.isArray(candidate.classifier_candidates)
      ? candidate.classifier_candidates
          .filter(
            (item): item is { intent: string; confidence: number } =>
              typeof item === "object" &&
              item !== null &&
              typeof (item as { intent?: unknown }).intent === "string" &&
              typeof (item as { confidence?: unknown }).confidence === "number"
          )
          .map((item) => ({
            intent: item.intent,
            confidence: item.confidence,
          }))
      : [],
    screen_context:
      typeof candidate.screen_context === "string"
        ? candidate.screen_context
        : "",
    screen_bias_applied: Boolean(candidate.screen_bias_applied),
    screen_bias_changed_primary: Boolean(candidate.screen_bias_changed_primary),
    pre_bias_primary_intent:
      typeof candidate.pre_bias_primary_intent === "string"
        ? candidate.pre_bias_primary_intent
        : "",
    node_statuses: candidate.node_statuses as Record<string, string>,
    merge_events: Array.isArray(candidate.merge_events)
      ? (candidate.merge_events as WorkflowDebugSummary["merge_events"])
      : [],
    debug_event_count:
      typeof candidate.debug_event_count === "number"
        ? candidate.debug_event_count
        : 0,
  };
}

export default function ChatBubble({ message, onSuggestionSelect, onActionClick }: Props) {
  const isUser = message.role === "user";
  const [expanded, setExpanded] = useState(false);
  // WIP-1 § 8-7: per-bubble undo state. Local because each autonomous-action
  // bubble has its own undo lifecycle; ChatScreen doesn't need to coordinate.
  const [undoState, setUndoState] = useState<"idle" | "pending" | "done" | "error">(
    "idle"
  );
  // Phase 11-5: per-bubble learning-card state. "That's not me" wires to
  // updatePreferenceMemory(id, {importance: 0.1}) so the visible affordance
  // reuses Phase 11-3.b's correction pipeline exactly — one semantic, two
  // UI vocabularies. Dismissed cards still log a downgrade so the inline
  // extractor's dedup gate stops re-extracting the same misread next turn.
  const [learningCardState, setLearningCardState] = useState<
    "idle" | "dismissing" | "dismissed" | "error"
  >("idle");
  const undoAction =
    !isUser && message.response_type === "action"
      ? readUndoAction(message.data)
      : null;
  const nextActions = !isUser ? message.next_actions ?? [] : [];
  const planDayAction = nextActions.find((action) => action.type === "plan_day");
  const learningCard = !isUser ? message.learning_card ?? null : null;

  // Resolve agent display
  const agentKey = message.agent_name || "";
  const agent = AGENT_CONFIG[agentKey] || DEFAULT_AGENT;
  const avatarColor = message.agent_color || agent.color;
  const workflowDebug = !isUser ? getWorkflowDebug(message.data) : null;
  const displayedIntent = !isUser
    ? (workflowDebug?.intents?.length ?? 0) > 1
      ? "blended"
      : (message.intent || "")
    : "";
  const displayedIntentLabel = !isUser
    ? displayedIntent === "blended"
      ? blendedIntentLabel(workflowDebug?.intents || [])
      : formatIntentLabel(displayedIntent)
    : "";
  const hasStructuredFollowUp = Boolean(
    !isUser && message.schedule_blocks && message.schedule_blocks.length > 0
  );
  const assistantCopy = !isUser
    ? buildAssistantCopyView(message.content, { hasStructuredFollowUp })
    : null;
  const renderedAssistantCopy =
    assistantCopy && assistantCopy.hidden && expanded
      ? `${assistantCopy.visible}\n\n${assistantCopy.hidden}`
      : assistantCopy?.visible ?? message.content;

  return (
    <div
      className={`flex ${isUser ? "justify-end" : "justify-start"} mb-3`}
      data-testid={isUser ? "chat-bubble-user" : "chat-bubble-assistant"}
      role={isUser ? undefined : "article"}
      aria-label={isUser ? undefined : "Assistant message"}
    >
      <div className={`max-w-[85%] ${isUser ? "order-2" : "order-1"}`}>
        {/* Agent header */}
        {!isUser && (
          <div className="flex items-center gap-1.5 mb-1">
            <div
              className="w-5 h-5 rounded-full flex items-center justify-center"
              style={{ backgroundColor: avatarColor }}
            >
              <span className="text-[9px] text-white font-bold">{agent.initial}</span>
            </div>
            <span className="text-[11px] text-muted font-medium">{agent.label}</span>
            {displayedIntent && (
              <span
                className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                  INTENT_COLORS[displayedIntent] || "bg-muted/10 text-muted"
                }`}
              >
                {displayedIntentLabel}
              </span>
            )}
          </div>
        )}

        {/* Message bubble */}
        <div
          className={`rounded-2xl px-4 py-3 text-[14px] leading-relaxed ${
            isUser
              ? "bg-sage-pale text-text rounded-br-sm rounded-tl-lg rounded-tr-lg rounded-bl-lg"
              : "bg-card border border-border text-text shadow-sm rounded-bl-sm rounded-tl-lg rounded-tr-lg rounded-br-lg"
          }`}
          data-testid={isUser ? "chat-bubble-user-content" : "chat-bubble-assistant-content"}
        >
          {isUser ? (
            <p>{message.content}</p>
          ) : (
            <div className="space-y-2">
              <div>
                <div className="space-y-0">
                  {renderMarkdown(renderedAssistantCopy)}
                  {assistantCopy?.hidden && (
                    <button
                      onClick={() => setExpanded((current) => !current)}
                      className="mt-2 text-[11px] font-medium text-forest hover:underline"
                    >
                      {expanded ? "Show less" : "More"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Schedule blocks from planning flow */}
        {!isUser && message.schedule_blocks && message.schedule_blocks.length > 0 && (
          <div className="mt-3 space-y-1.5">
            <p className="text-[10px] font-medium text-muted uppercase tracking-wider">Proposed Plan</p>
            {message.schedule_blocks.map((block, i) => {
              const start = new Date(block.start_time);
              const end = new Date(block.end_time);
              const timeStr = `${formatTime(start)} – ${formatTime(end)}`;
              return (
                <div
                  key={i}
                  className="flex items-start gap-2 p-2 rounded-lg bg-sage-pale/30 border border-border/50"
                >
                  <div
                    className="w-1 rounded-full self-stretch flex-shrink-0"
                    style={{ backgroundColor: block.color || "#6366F1" }}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-text">{block.title}</p>
                    <p className="text-[10px] text-muted">
                      {timeStr} · {block.duration_min}m
                      {block.goal_title && ` · 🎯 ${block.goal_title}`}
                    </p>
                    {block.rationale && (
                      <p className="text-[10px] text-sage mt-0.5 italic">{block.rationale}</p>
                    )}
                  </div>
                </div>
              );
            })}
            {onActionClick && (
              <div className="flex gap-2 mt-2">
                <button
                  onClick={() =>
                    onActionClick(
                      planDayAction?.id || "confirm_plan",
                      planDayAction?.type || "confirm_plan"
                    )
                  }
                  className="flex-1 py-2 rounded-full bg-forest-med text-white text-xs font-medium
                    hover:bg-forest transition-colors"
                >
                  Confirm &amp; Add to Plan
                </button>
                <button
                  onClick={() => onSuggestionSelect?.("Adjust this plan — less Career, more Health")}
                  className="px-3 py-2 rounded-full border border-border text-muted text-xs font-medium
                    hover:text-text transition-colors"
                >
                  Adjust
                </button>
              </div>
            )}
          </div>
        )}

        {/* Undo chip for autonomous actions (WIP-1 § 8-7). Renders when the
            backend marked the response as an action and emitted an
            undo_action payload. Click POSTs /api/chat/undo and reverses the
            entity insert; on success we replace the button with an
            "Undone" pill so the user has clear confirmation. */}
        {undoAction && undoState !== "done" && (
          <div className="mt-2">
            <button
              type="button"
              data-testid="chat-undo-btn"
              disabled={undoState === "pending"}
              onClick={async () => {
                setUndoState("pending");
                try {
                  await undoChatAction({
                    action_type: undoAction.type,
                    entity_id: undoAction.entity_id,
                    extra: undoAction.extra,
                  });
                  // Undo reverses a goal/task/block server-side; fire all three
                  // notifiers so every dependent surface drops the undone row
                  // instead of showing it until a tab remount (audit 2026-05-29).
                  const { getState } = useDataRefresh;
                  getState().notifyTaskChange();
                  getState().notifyGoalChange();
                  getState().notifyScheduleChange();
                  setUndoState("done");
                } catch {
                  setUndoState("error");
                }
              }}
              className="text-[11px] font-medium px-3 py-1 rounded-full border border-border bg-cream text-text
                hover:bg-sage-pale/40 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
              aria-label={undoAction.label || "Undo"}
            >
              {undoState === "pending"
                ? "Undoing…"
                : undoState === "error"
                  ? "Retry undo"
                  : undoAction.label || "Undo"}
            </button>
          </div>
        )}
        {undoAction && undoState === "done" && (
          <div className="mt-2">
            <span
              data-testid="chat-undo-done"
              className="text-[11px] font-medium px-3 py-1 rounded-full bg-sage-pale/40 text-forest"
            >
              Undone
            </span>
          </div>
        )}

        {/* Generic next_actions strip (WIP-1 § 8-7, secondary affordance).
            Empty for the create_goal path because Coach commits autonomously,
            but kept so future ActionTypes that DO emit next_actions render
            without extra wiring. Hidden when the schedule_blocks panel
            already owns the action area to avoid double buttons. */}
        {nextActions.length > 0 &&
          !(message.schedule_blocks && message.schedule_blocks.length > 0) &&
          onActionClick && (
            <div className="mt-2 flex flex-wrap gap-2">
              {nextActions.map((action, idx) => {
                const id = action.id || `${action.type}-${idx}`;
                return (
                  <button
                    key={id}
                    type="button"
                    data-testid={`chat-action-${action.type}`}
                    onClick={() => onActionClick(id, action.type)}
                    className="text-[11px] font-medium px-3 py-1 rounded-full bg-forest-med text-white
                      hover:bg-forest transition-colors"
                  >
                    {action.label || action.type}
                  </button>
                );
              })}
            </div>
          )}

        {/* Phase 11-5: visible learning card. Renders only on assistant
            turns where the inline extractor wrote a high-importance new
            row (server-side threshold importance >= 0.6). One card per
            assistant reply, never on user turns. "That's not me" wires
            to updatePreferenceMemory(id, {importance: 0.1}) so the
            visible affordance reuses Phase 11-3.b's downgrade pipeline
            exactly. */}
        {learningCard && learningCardState !== "dismissed" && (
          <div
            className="mt-2 rounded-xl border border-sage-pale bg-sage-pale/30 px-3 py-2"
            data-testid="chat-learning-card"
          >
            <p className="text-[11px] uppercase tracking-wide text-muted">
              I just noticed
            </p>
            <p
              className="text-xs text-text mt-0.5"
              data-testid="chat-learning-card-content"
            >
              {learningCard.content}
            </p>
            <div className="mt-1 flex gap-2">
              <button
                type="button"
                disabled={learningCardState === "dismissing"}
                data-testid="chat-learning-card-dismiss"
                onClick={async () => {
                  setLearningCardState("dismissing");
                  try {
                    await updatePreferenceMemory(learningCard.id, {
                      importance: 0.1,
                    });
                    setLearningCardState("dismissed");
                  } catch {
                    setLearningCardState("error");
                  }
                }}
                className="text-[11px] font-medium px-2 py-0.5 rounded-full text-text-light hover:underline disabled:opacity-40"
              >
                {learningCardState === "dismissing"
                  ? "…"
                  : learningCardState === "error"
                    ? "Try again"
                    : "That's not me"}
              </button>
            </div>
          </div>
        )}
        {learningCard && learningCardState === "dismissed" && (
          <div className="mt-2">
            <span
              data-testid="chat-learning-card-dismissed"
              className="text-[11px] font-medium px-3 py-1 rounded-full bg-sand-pale/40 text-text-light"
            >
              Got it — I&apos;ll downplay that.
            </span>
          </div>
        )}

        {/* Timestamp */}
        <div className={`text-[10px] text-muted mt-1 ${isUser ? "text-right" : "text-left"}`}>
          {formatTime(message.timestamp)}
        </div>
      </div>
    </div>
  );
}
